import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowLeft, BarChart3, Target, TrendingUp } from 'lucide-react';
import type { EnterpriseAction } from '../../../services/actionService';
import type { Indicator } from '../../../types/indicator';
import {
  LST_CARGO_LABELS,
  type LstCargo,
  type LstExecucao,
  type LstMilestone,
  type LstTemplate,
} from '../types';
import { computeLstAnalytics, datesBetween, type LstSubject } from '../services/lstAnalytics';
import { filterTemplatesForUser } from '../utils/lstTemplateScope';
import { templateHasLinkedIndicator } from '../utils/lstIndicator';
import { IndicatorService } from '../../../services/indicatorService';
import {
  aggregateResultScores,
  computeEfficiencyMatrix,
  computeExecutionResultScore,
  computeIndicatorResultScore,
  hasExplicitLinkedIndicator,
  pickLinkedIndicators,
  resolveLinkedIndicatorLabel,
} from '../services/lstCorrelation';
import { toLocalISODate } from '../services/lstService';
import { LstEfficiencyMatrix } from './LstEfficiencyMatrix';
import { computeIel, collectLinkedIndicatorAssessments } from '../services/ielService';
import { computeTplMaturityModel, lastNDaysDates } from '../services/tplMaturityModel';
import { toTplMaturityPresentation } from '../utils/tplMaturityPresentation';
import { TplIelHero } from './tpl/ux/TplIelHero';
import { TplExecutiveJudgmentCards } from './tpl/ux/TplExecutiveJudgmentCards';
import { buildExecutiveJudgments } from '../services/tplExecutiveJudgment';
import { computeTplOeeMetrics } from '../services/lstOeeMetrics';
import { buildDataflowExecutiveRibbon } from '../services/dataflowExecutiveDiagnostics';
import { computeDataflowInsights } from '../services/dataflowInsightEngine';
import { buildDataflowCopilotActions } from '../services/dataflowCopilotActions';
import type { TplManagementHubData } from '../services/tplManagementHubInsights';
import type { LstScreen } from './LstScreenTabs';
import { RotinaLinkedIndicatorsPanel } from './RotinaLinkedIndicatorsPanel';
import { DataflowExecutiveDiagnosis } from './dataflow/DataflowExecutiveDiagnosis';
import { DataflowInsightEngine } from './dataflow/DataflowInsightEngine';
import { DataflowCompactPillars, buildExecutiveCompactPillars } from './dataflow/DataflowCompactPillars';
import { DataflowLossRanking } from './dataflow/DataflowLossRanking';
import { DataflowLayerLeaderboard } from './dataflow/DataflowLayerLeaderboard';
import { DataflowAttentionNow } from './dataflow/DataflowAttentionNow';
import { DataflowCopilot } from './dataflow/DataflowCopilot';

interface LstAnalyticsDashboardProps {
  onClose?: () => void;
  organizationId: string;
  isManager: boolean;
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  subjects: LstSubject[];
  selfUserId: string;
  departments: Array<{ id: string; name: string }>;
  indicators: Indicator[];
  milestones: LstMilestone[];
  today: string;
  loading: boolean;
  leaders?: Array<{ userId: string; name: string }>;
  correctiveActions?: EnterpriseAction[];
  hub?: TplManagementHubData | null;
  onNavigate?: (screen: LstScreen) => void;
}

type PeriodOpt = 30 | 90 | 180;

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--bg-surface, white)',
  border: '1px solid var(--border-default, #e5e7eb)',
  borderRadius: '6px',
  fontSize: '11px',
  color: 'var(--text-primary, #0f172a)',
};
const GRID = 'rgba(148, 163, 184, 0.15)';
const TICK = 'var(--text-secondary, #64748b)';
const LAYER_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6'];

function isoMinusDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() - n);
  return toLocalISODate(dt);
}

function monthKeysFrom(dates: string[]): Array<{ key: string; label: string }> {
  const seen = new Set<string>();
  const out: Array<{ key: string; label: string }> = [];
  for (const d of dates) {
    const k = d.slice(0, 7);
    if (seen.has(k)) continue;
    seen.add(k);
    const [yy, mm] = k.split('-').map(Number);
    const label = new Date(yy, mm - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    out.push({ key: k, label });
  }
  return out;
}

/** Torre de Controle — Diagnóstico unificado (Dashboard + Análise). */
export const LstAnalyticsDashboard: React.FC<LstAnalyticsDashboardProps> = ({
  onClose,
  organizationId,
  isManager,
  templates,
  execucoes,
  subjects,
  selfUserId,
  departments,
  indicators,
  milestones,
  today,
  loading,
  leaders = [],
  correctiveActions = [],
  hub = null,
  onNavigate,
}) => {
  const [period, setPeriod] = useState<PeriodOpt>(90);
  const [cargoFilter, setCargoFilter] = useState<'all' | LstCargo>('all');
  const [setorFilter, setSetorFilter] = useState<'all' | string>('all');

  const deptName = useMemo(() => new Map(departments.map((d) => [d.id, d.name])), [departments]);

  const dates = useMemo(() => datesBetween(isoMinusDays(today, period), today), [today, period]);
  const monthKeys = useMemo(() => monthKeysFrom(dates), [dates]);

  const cargoOptions = useMemo(() => {
    const set = new Set<LstCargo>();
    subjects.forEach((s) => set.add(s.cargo));
    return Array.from(set);
  }, [subjects]);

  const setorOptions = useMemo(() => {
    const set = new Set<string>();
    subjects.forEach((s) => {
      if (s.member?.departmentId) set.add(s.member.departmentId);
    });
    return Array.from(set);
  }, [subjects]);

  const filteredSubjects = useMemo(
    () =>
      subjects.filter(
        (s) =>
          (cargoFilter === 'all' || s.cargo === cargoFilter) &&
          (setorFilter === 'all' || s.member?.departmentId === setorFilter),
      ),
    [subjects, cargoFilter, setorFilter],
  );

  const selfSubject = useMemo(() => subjects.find((s) => s.userId === selfUserId), [subjects, selfUserId]);

  // Minha evolução (self).
  const selfAnalytics = useMemo(
    () =>
      computeLstAnalytics({
        templates,
        execucoes,
        subjects: selfSubject ? [selfSubject] : [],
        dates,
        monthKeys,
        today,
      }),
    [templates, execucoes, selfSubject, dates, monthKeys, today],
  );

  // Escopo (Pareto + Performance por Camada).
  const scopeAnalytics = useMemo(
    () =>
      computeLstAnalytics({
        templates,
        execucoes,
        subjects: filteredSubjects,
        dates,
        monthKeys,
        today,
      }),
    [templates, execucoes, filteredSubjects, dates, monthKeys, today],
  );

  const paretoTop = scopeAnalytics.pareto.slice(0, 8);
  const layerRows = scopeAnalytics.perMember;

  const milestoneByMonth = useMemo(() => {
    const map = new Map<string, LstMilestone>();
    for (const m of milestones) {
      const key = m.milestone_date.slice(0, 7);
      if (!map.has(key)) map.set(key, m);
    }
    return map;
  }, [milestones]);

  const trendEnriched = useMemo(
    () =>
      selfAnalytics.trend.map((p) => ({
        ...p,
        milestone: milestoneByMonth.get(p.key) ?? null,
      })),
    [selfAnalytics.trend, milestoneByMonth],
  );

  /** Rotinas do usuário logado (cargo + exceções individuais). */
  const selfTemplates = useMemo(
    () =>
      selfSubject
        ? filterTemplatesForUser(templates, selfSubject.cargo, selfSubject.userId)
        : [],
    [templates, selfSubject],
  );

  const linkedIndicators = useMemo(
    () => pickLinkedIndicators(selfTemplates, indicators),
    [selfTemplates, indicators],
  );

  const periodStart = dates[0] ?? today;
  const periodEnd = dates[dates.length - 1] ?? today;

  const { data: indicatorSnapshots = [], isLoading: snapshotsLoading } = useQuery({
    queryKey: [
      'lst',
      'indicator-snapshots',
      organizationId,
      linkedIndicators.map((i) => i.id).join(','),
      periodStart,
      periodEnd,
    ],
    queryFn: async () => {
      const batches = await Promise.all(
        linkedIndicators.map((ind) =>
          IndicatorService.getSnapshots(organizationId, ind.id, 200),
        ),
      );
      return batches.flat();
    },
    enabled: !!organizationId && linkedIndicators.length > 0,
    staleTime: 60_000,
  });

  const selfAdherencePct = useMemo(() => {
    if (!selfSubject) return scopeAnalytics.totals.pct;
    const selfOnly = computeLstAnalytics({
      templates,
      execucoes,
      subjects: [selfSubject],
      dates,
      monthKeys,
      today,
    });
    return selfOnly.totals.pct;
  }, [selfSubject, templates, execucoes, dates, monthKeys, today, scopeAnalytics.totals.pct]);

  const executionResultScore = useMemo(
    () =>
      selfUserId
        ? computeExecutionResultScore(
            selfTemplates,
            execucoes,
            indicators,
            selfUserId,
            periodStart,
            periodEnd,
          )
        : null,
    [selfTemplates, execucoes, indicators, selfUserId, periodStart, periodEnd],
  );

  const snapshotResultScore = useMemo(() => {
    if (linkedIndicators.length === 0) return null;
    const scores = linkedIndicators.map((ind) =>
      computeIndicatorResultScore(
        ind,
        indicatorSnapshots.filter((s) => s.indicator_id === ind.id),
        periodStart,
        periodEnd,
      ),
    );
    return aggregateResultScores(scores);
  }, [linkedIndicators, indicatorSnapshots, periodStart, periodEnd]);

  const resultScore = useMemo(() => {
    if (executionResultScore?.achievementPct != null) return executionResultScore;
    return snapshotResultScore;
  }, [executionResultScore, snapshotResultScore]);

  const templateLinkLabel = useMemo(
    () => resolveLinkedIndicatorLabel(selfTemplates, indicators),
    [selfTemplates, indicators],
  );

  const efficiencyMatrix = useMemo(
    () =>
      computeEfficiencyMatrix(
        selfAdherencePct,
        linkedIndicators,
        resultScore,
        hasExplicitLinkedIndicator(selfTemplates) ? templateLinkLabel : null,
      ),
    [selfAdherencePct, linkedIndicators, resultScore, selfTemplates, templateLinkLabel],
  );

  const weekDates = useMemo(() => dates.slice(-7), [dates]);
  const sustainDates = useMemo(() => lastNDaysDates(Math.min(90, period), today), [today, period]);

  const orgLeaders = useMemo(
    () =>
      isManager && leaders.length > 0
        ? leaders
        : subjects.map((s) => ({ userId: s.userId, name: s.name })),
    [isManager, leaders, subjects],
  );

  const maturityModel = useMemo(
    () =>
      computeTplMaturityModel({
        templates,
        execucoes,
        weekDates,
        monthDates: dates,
        sustainDates,
        today,
        leaders: orgLeaders,
        indicators,
        indicatorSnapshots,
        correctiveActions,
      }),
    [
      templates,
      execucoes,
      weekDates,
      dates,
      sustainDates,
      today,
      orgLeaders,
      indicators,
      indicatorSnapshots,
      correctiveActions,
    ],
  );

  const maturity = useMemo(() => toTplMaturityPresentation(maturityModel), [maturityModel]);

  const iel = useMemo(
    () =>
      computeIel({
        templates,
        execucoes,
        dates: weekDates,
        indicators,
        indicatorSnapshots,
      }),
    [templates, execucoes, weekDates, indicators, indicatorSnapshots],
  );

  const linkedAssessments = useMemo(
    () =>
      collectLinkedIndicatorAssessments(
        templates.filter((t) => t.active),
        execucoes,
        indicators,
        indicatorSnapshots,
        periodStart,
        periodEnd,
        isoMinusDays(periodStart, 7),
        isoMinusDays(periodEnd, 7),
      ),
    [templates, execucoes, indicators, indicatorSnapshots, periodStart, periodEnd],
  );

  const weeklyDisciplina = maturityModel.pillarDetails.disciplina.weeklyEvolution ?? [];
  const monthlyDisciplina = maturityModel.pillarDetails.disciplina.monthlyEvolution ?? [];

  const scopeOee = useMemo(
    () => computeTplOeeMetrics(templates, execucoes, weekDates),
    [templates, execucoes, weekDates],
  );

  const analyticsJudgments = useMemo(
    () => buildExecutiveJudgments(scopeOee, iel, maturity.pillars.escalonamento),
    [scopeOee, iel, maturity.pillars.escalonamento],
  );

  const executiveRibbon = useMemo(
    () =>
      buildDataflowExecutiveRibbon({
        ielScore: iel.score,
        maturityScore: maturity.score,
        disciplinaPct: scopeAnalytics.totals.pct,
        riskCount: maturityModel.risks.length,
        lossCount: scopeAnalytics.totals.perdido,
        actionCount: scopeAnalytics.totals.desviosAbertos,
        criticalLossCount: paretoTop.filter((p) => p.isCritical).length,
        plannerConflicts: hub?.totalConflicts ?? 0,
        escalationsOverdue: hub?.escalatedChains.length ?? 0,
      }),
    [iel, maturity, scopeAnalytics.totals, maturityModel.risks, paretoTop, hub],
  );

  const dataflowInsights = useMemo(
    () =>
      computeDataflowInsights({
        disciplinaPct: scopeAnalytics.totals.pct,
        ielScore: iel.score,
        topLossLabels: paretoTop.slice(0, 3).map((p) => p.title),
        topLossCounts: paretoTop.slice(0, 3).map((p) => p.ocorrencias),
        totalLosses: scopeAnalytics.totals.perdido,
        mainRiskLabel: maturityModel.risks[0] ?? null,
      }),
    [scopeAnalytics.totals, iel.score, paretoTop, maturityModel.risks],
  );

  const lossRankItems = useMemo(
    () =>
      paretoTop.map((p, i) => ({
        rank: i + 1,
        title: p.title,
        count: p.ocorrencias,
        isCritical: p.isCritical,
      })),
    [paretoTop],
  );

  const compactPillars = useMemo(
    () =>
      buildExecutiveCompactPillars({
        iel: iel.score,
        maturidade: maturity.score,
        disciplina: maturity.pillars.disciplina,
        conformidade: maturity.pillars.conformidade,
        escalonamento: maturity.pillars.escalonamento,
      }),
    [iel.score, maturity],
  );

  const rotinasSemKpi = useMemo(
    () => templates.filter((t) => t.active && !templateHasLinkedIndicator(t)).length,
    [templates],
  );

  const copilotActions = useMemo(
    () =>
      buildDataflowCopilotActions({
        hub,
        disciplinaPct: scopeAnalytics.totals.pct,
        rotinasSemKpi,
        escalonamentosVencidos: hub?.escalatedChains.length ?? 0,
        onNavigate,
      }),
    [hub, scopeAnalytics.totals.pct, rotinasSemKpi, onNavigate],
  );

  const layerLeaderRows = useMemo(
    () =>
      layerRows.map((r) => ({
        userId: r.userId,
        name: r.name,
        disciplinaPct: r.pct,
        conformidadePct: r.conformidade,
        perdas: r.perdido,
        isSelf: r.userId === selfUserId,
      })),
    [layerRows, selfUserId],
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-[var(--bg-app)]">
      <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-2.5 dark:border-slate-800">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-white dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            <ArrowLeft size={14} /> Voltar
          </button>
        )}
        <div className="mr-auto">
          <h2 className="flex items-center gap-2 text-[15px] font-bold text-slate-800 dark:text-slate-100">
            <BarChart3 size={18} className="text-blue-500" /> Diagnóstico
          </h2>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Julgamento · Causas · Perdas · Camadas · Indicadores
          </p>
        </div>

        <div className="inline-flex overflow-hidden rounded-[5px] border border-slate-200 dark:border-slate-700">
          {([30, 90, 180] as PeriodOpt[]).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                period === p ? 'bg-[#2F6FED] text-white' : 'bg-white text-slate-500 hover:bg-slate-50 dark:bg-[var(--bg-surface)] dark:text-slate-400'
              }`}
            >
              {p}d
            </button>
          ))}
        </div>

        <select
          value={cargoFilter}
          onChange={(e) => setCargoFilter(e.target.value as 'all' | LstCargo)}
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
        >
          <option value="all">Todos os cargos</option>
          {cargoOptions.map((c) => (
            <option key={c} value={c}>{LST_CARGO_LABELS[c]}</option>
          ))}
        </select>

        {setorOptions.length > 0 && (
          <select
            value={setorFilter}
            onChange={(e) => setSetorFilter(e.target.value)}
            className="h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px] text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-200"
          >
            <option value="all">Todos os setores</option>
            {setorOptions.map((id) => (
              <option key={id} value={id}>{deptName.get(id) ?? `Setor ${id.slice(0, 4)}`}</option>
            ))}
          </select>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-6">
        {loading || snapshotsLoading ? (
          <p className="py-16 text-center text-[12px] text-slate-400">Carregando diagnóstico…</p>
        ) : (
          <div className="mx-auto flex max-w-5xl flex-col gap-3">
            {/* Nível 1 — Julgamento Executivo */}
            <DataflowExecutiveDiagnosis ribbon={executiveRibbon} />
            <DataflowCompactPillars pillars={compactPillars} />

            {/* Nível 2 — Insight Executivo */}
            <DataflowInsightEngine insights={dataflowInsights} />

            {/* Nível 3 — O que exige atenção agora */}
            <DataflowAttentionNow hub={hub} />

            {/* Nível 4 — Causas */}
            <section>
              <p className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">Causas</p>
              <TplExecutiveJudgmentCards cards={analyticsJudgments} compact />
            </section>

            {/* Nível 5 — Top Perdas */}
            <section>
              <p className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">Top Perdas</p>
              <DataflowLossRanking
                items={lossRankItems}
                totalLosses={scopeAnalytics.totals.perdido}
                criticalCount={paretoTop.filter((p) => p.isCritical).length}
              />
              <Panel title="Pareto de Perdas por Rotina" icon={<BarChart3 size={15} className="text-amber-500" />}>
                {paretoTop.length === 0 ? (
                  <EmptyChart label="Sem desvios ou perdas no período." />
                ) : (
                  <>
                    <div style={{ height: 260 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={paretoTop} margin={{ top: 10, right: 12, left: -20, bottom: 0 }}>
                          <CartesianGrid vertical={false} stroke={GRID} strokeDasharray="1 5" />
                          <XAxis dataKey="title" axisLine={false} tickLine={false} tick={{ fill: TICK, fontSize: 9 }} interval={0} angle={-12} textAnchor="end" height={48} />
                          <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fill: TICK, fontSize: 10 }} allowDecimals={false} />
                          <YAxis yAxisId="right" orientation="right" domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: TICK, fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                          <Tooltip contentStyle={TOOLTIP_STYLE} />
                          <Bar yAxisId="left" dataKey="ocorrencias" name="Ocorrências" radius={[3, 3, 0, 0]}>
                            {paretoTop.map((p, i) => (
                              <Cell key={i} fill={p.isCritical ? '#ef4444' : '#f59e0b'} />
                            ))}
                          </Bar>
                          <Line yAxisId="right" type="monotone" dataKey="pctAccum" name="% Acumulado" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    <Legend2 items={[{ c: '#ef4444', l: 'Crítica' }, { c: '#f59e0b', l: 'Normal' }, { c: '#2563eb', l: '% acumulado' }]} />
                  </>
                )}
              </Panel>
              {paretoTop.length > 0 && (
                <Panel title="Detalhamento" icon={<BarChart3 size={15} className="text-red-500" />}>
                  <ul className="space-y-1">
                    {paretoTop.map((p) => (
                      <li key={p.templateId} className="flex items-center justify-between rounded border border-slate-100 px-2 py-1 text-[11px] dark:border-slate-800">
                        <span className="truncate font-medium text-slate-700 dark:text-slate-200">{p.title}</span>
                        <span className="shrink-0 tabular-nums text-red-600">{p.ocorrencias} ocorr.</span>
                      </li>
                    ))}
                  </ul>
                </Panel>
              )}
            </section>

            {/* Nível 6 — Camadas */}
            <section>
              <p className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                Quem está sustentando o sistema?
              </p>
              {!isManager ? (
                <EmptyChart label="Ranking por camada disponível para gestores com liderados." />
              ) : (
                <>
                  <DataflowLayerLeaderboard rows={layerLeaderRows} />
                  {layerRows.length > 0 && (
                    <details className="mt-2 rounded-lg border border-slate-200 dark:border-slate-800">
                      <summary className="cursor-pointer px-3 py-2 text-[10px] font-bold uppercase text-slate-500">
                        Evidência — gráfico comparativo
                      </summary>
                      <div className="border-t border-slate-100 p-3 dark:border-slate-800">
                        <div style={{ height: Math.max(140, layerRows.length * 30) }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart layout="vertical" data={layerRows} margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                              <CartesianGrid horizontal={false} stroke={GRID} strokeDasharray="1 5" />
                              <XAxis type="number" domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: TICK, fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                              <YAxis type="category" dataKey="name" width={110} axisLine={false} tickLine={false} tick={{ fill: TICK, fontSize: 10 }} />
                              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`, 'Disciplina']} />
                              <Bar dataKey="pct" radius={[0, 3, 3, 0]} barSize={14}>
                                {layerRows.map((r, i) => (
                                  <Cell key={r.userId} fill={LAYER_COLORS[i % LAYER_COLORS.length]} />
                                ))}
                              </Bar>
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </details>
                  )}
                </>
              )}
            </section>

            {/* Nível 7 — Indicadores */}
            <section>
              <p className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">Indicadores</p>
              <Panel title="Correlação Rotina × Resultado" icon={<Target size={15} className="text-blue-500" />}>
                {linkedAssessments.length === 0 ? (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-[11px] text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                    Existem rotinas sem indicador vinculado. O impacto operacional não pode ser medido.
                  </p>
                ) : (
                  <>
                    <LstEfficiencyMatrix result={efficiencyMatrix} />
                    <RotinaLinkedIndicatorsPanel assessments={linkedAssessments} />
                  </>
                )}
              </Panel>
            </section>

            {/* Ações — Copiloto DataFlow */}
            <DataflowCopilot actions={copilotActions} />

            {/* Evidências — recolhidas por padrão */}
            <details className="rounded-lg border border-slate-200 dark:border-slate-800">
              <summary className="cursor-pointer px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                Evidências — gráficos e matriz
              </summary>
              <div className="space-y-3 border-t border-slate-100 p-3 dark:border-slate-800">
                <TplIelHero iel={iel} compact audit />
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <Panel title="Disciplina — evolução semanal" icon={<TrendingUp size={15} className="text-blue-500" />}>
                    <MiniLineChart data={weeklyDisciplina} label="Disciplina" />
                  </Panel>
                  <Panel title="Disciplina — evolução mensal" icon={<TrendingUp size={15} className="text-blue-500" />}>
                    <MiniLineChart data={monthlyDisciplina} label="Disciplina" />
                  </Panel>
                </div>
                <Panel title="Tendência Disciplina × Conformidade" icon={<TrendingUp size={15} className="text-blue-500" />}>
                  <div style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={trendEnriched} margin={{ top: 16, right: 12, left: -18, bottom: 0 }}>
                        <defs>
                          <linearGradient id="lstTrend" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid vertical={false} stroke={GRID} strokeDasharray="1 5" />
                        <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: TICK, fontSize: 10 }} />
                        <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: TICK, fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                        <Tooltip content={<MilestoneTooltip />} />
                        <Area type="monotone" dataKey="pct" stroke="#2563eb" strokeWidth={2} fill="url(#lstTrend)" name="Disciplina" />
                        <Line type="monotone" dataKey="conformidade" stroke="#10b981" strokeWidth={1.5} dot={false} name="Conformidade" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </Panel>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

/** Alias export — Torre de Controle Diagnóstico. */
export const TplDiagnosticoPanel = LstAnalyticsDashboard;

function MiniLineChart({ data, label }: { data: Array<{ label: string; pct: number }>; label: string }) {
  if (data.length === 0) {
    return <EmptyChart label="Sem dados no período." />;
  }
  return (
    <div style={{ height: 160 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="label" tick={{ fontSize: 9 }} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} width={28} />
          <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: number) => [`${v}%`, label]} />
          <Area type="monotone" dataKey="pct" stroke="#2F6FED" fill="#2F6FED" fillOpacity={0.15} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Subcomponentes ─────────────────────────────────────────

const MilestoneTooltip: React.FC<{ active?: boolean; payload?: Array<{ payload: { pct: number; conformidade: number; milestone?: LstMilestone | null } }> }> = ({
  active,
  payload,
}) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={TOOLTIP_STYLE} className="max-w-[220px] px-2 py-1.5">
      <p className="text-[11px] font-semibold">Disciplina: {p.pct}%</p>
      <p className="text-[10px] text-slate-500">Conformidade: {p.conformidade}%</p>
      {p.milestone && (
        <div className="mt-1 border-t border-slate-100 pt-1 dark:border-slate-700">
          <p className="text-[10px] font-bold text-violet-600 dark:text-violet-400">◆ {p.milestone.label}</p>
          <p className="text-[10px] leading-snug text-slate-600 dark:text-slate-300">{p.milestone.notes}</p>
        </div>
      )}
    </div>
  );
};

const KpiCard: React.FC<{
  label: string;
  value: React.ReactNode;
  tone: string;
  hint?: string;
}> = ({ label, value, tone, hint }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-800 dark:bg-slate-950">
    <div className={`text-[22px] font-bold leading-none tabular-nums ${tone}`}>{value}</div>
    <div className="mt-1 text-[9px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    {hint && <div className="mt-0.5 text-[8px] text-slate-400">{hint}</div>}
  </div>
);

const Panel: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
    <h3 className="mb-2 flex items-center gap-1.5 text-[12px] font-bold text-slate-700 dark:text-slate-200">
      {icon} {title}
    </h3>
    {children}
  </div>
);

const Legend2: React.FC<{ items: Array<{ c: string; l: string }> }> = ({ items }) => (
  <div className="mt-2 flex flex-wrap items-center justify-center gap-3 text-[10px] text-slate-500 dark:text-slate-400">
    {items.map((it) => (
      <span key={it.l} className="inline-flex items-center gap-1">
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: it.c }} /> {it.l}
      </span>
    ))}
  </div>
);

const EmptyChart: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex h-[200px] items-center justify-center text-center text-[12px] text-slate-400">{label}</div>
);

export default LstAnalyticsDashboard;
